<template>
    <div id="settings-page-v2">
        <AppBreadCrumb :paths="[{ title: $t('message.settings') }]" :show-trial-remain-day="true" />
        <div class="settings-page-v2-wrapper">
            <SettingsMenu v-model="tab" />
            <div class="page-content" v-if="settingInitialized">
                <Company v-if="tab === 'company'" :update-settings="updateSingleSettings" />
                <Features v-if="tab === 'features'" :update-settings="updateSingleSettings" />
                <Language v-if="tab === 'language'" :update-settings="updateSingleSettings" />
                <BubbleSettings v-if="tab === 'bubble-settings'" :update-settings="updateSingleSettings" />
                <BubbleActions v-if="tab === 'bubble-actions'" :update-settings="updateSingleSettings" />
                <ChatThemeSettings v-if="tab === 'theme-settings'" :update-settings="updateSingleSettings" />
                <ChatTimingSettings v-if="tab === 'timing_settings'" :update-settings="updateSingleSettings" />
                <VisitorLoginForm v-if="tab === 'visitorLoginForm'" :update-settings="updateSingleSettings" />
                <Availability v-if="tab === 'availability'" :update-settings="updateSingleSettings" />
                <WebSiteIntegration v-if="tab === 'website-integration'" :update-settings="updateSingleSettings" class="website-integrations" />
                <ApiAccess v-if="tab === 'developer'" :update-settings="updateSingleSettings" />
                <VisitorAttribute v-if="tab === 'visitorAttribute'" />
                <AutoChatAssign v-if="tab === 'auto_assign'" :update-settings="updateSingleSettings" />
                <Wallet v-if="tab === 'wallet'" :update-settings="updateSingleSettings" />
            </div>
        </div>
    </div>
</template>

<script>
import SettingsMenu from "./SettingsMenu.vue";
import "./settings.scss";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import log2 from "../../../utils/log2";
import { sendSocketMessage } from "../../../utils/socketStream";
import Company from "./pages/Company.vue";
import Features from "./pages/Features.vue";
import Language from "./pages/Language.vue";
import BubbleSettings from "./pages/BubbleSettings.vue";
import BubbleActions from "./pages/BubbleActions.vue";
import ChatTimingSettings from "./pages/ChatTimingSettings.vue";
import ApiAccess from "./pages/ApiAccess.vue";
import WebSiteIntegration from "./pages/WebSiteIntegration.vue";
import ChatThemeSettings from "./pages/ChatThemeSettings.vue";
import Availability from "./pages/Availability.vue";
import VisitorLoginForm from "./pages/VisitorLoginForm.vue";
import SettingsMenuTypes from "./SettingsMenuTypes";
import VisitorAttribute from "./pages/VisitorAttribute.vue";
import AutoChatAssign from "./pages/AutoChatAssign.vue";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";
import systemPubSub from "../../../utils/systemPubSub";
import Wallet from "./pages/Wallet.vue";

import { filter } from "rxjs/operators";

const log = log2("Settings");
export default {
    name: "Settings",
    components: {
        AutoChatAssign,
        AppBreadCrumb,
        SettingsMenu,
        Company,
        Features,
        Language,
        BubbleSettings,
        BubbleActions,
        ChatTimingSettings,
        ApiAccess,
        WebSiteIntegration,
        ChatThemeSettings,
        Availability,
        VisitorLoginForm,
        VisitorAttribute,
        Wallet,
    },
    data() {
        const path = this.$route.path.replace("/settings/", "");
        const selected = Object.values(SettingsMenuTypes).find((x) => x.path === path);
        return {
            tab: selected ? selected.id : "company",
            editMode: false,
            isV2Theme: false,
            isVersionV2: false,
            systemPubSubs: [],
            handleSave: _.debounce((newSettings) => {
                const isValid = Date.now() - (this.mountedTime || Date.now()) > 2000; // defaulttan gerçeğe geçerken bir kere sunucuya gitmesini engellemek için alternatif başka çözüm var sa yapalım
                if (isValid) {
                    this.submitClicked = true;
                    const self = this;
                    this.$validator.validateAll().then((response) => {
                        if (response) {
                            this.publishSettings(newSettings);
                        } else {
                            self.$message.error({
                                content: self.$t("message.settingFormWrong"),
                                duration: 2,
                                key: "saved",
                            });
                        }
                    });
                }
            }, 750).bind(this),
        };
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            settingInitialized: (state) => state.setting.initialized,
            site: (state) => state.site.site,
            languages: (state) => state.language.languages,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            isIdeaSoft: (state) => _.get(state.site.site, "partner.name") === "ideasoft",
            isAdmin: (state) => state.auth.adminUser != null,
            isProPlanUser: (state) => _.get(state.setting.settings, "plan.selectedPlan") === "pro",
            systemLanguages: (state) => state.setting.settings.systemLanguages,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
    },
    methods: {
        ...mapActions({
            getSettings: "setting/getSettings",
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),
        publishSettings(settings) {
            this.updateSettings(settings)
                .then(() => {
                    sendSocketMessage("admin settings-updated", {
                        siteId: this.site._id,
                    });
                    this.$message.success({
                        content: this.$t("message.settingsSaved"),
                        duration: 2,
                        key: "saved",
                    });
                })
                .catch((error) => {
                    this.$message.error({
                        content:this.$t("message.settingsCannotSaved"),
                        duration: 2,
                        key: "saveFailed",
                    });
                })
                .finally(() => {
                    this.submitClicked = false;
                });
        },
        updateSingleSettings(key, value, immediately = false) {
            if (this.checkPermission(PERMISSION_TYPES["setting.update"].tr.name)) {
                this.setValue(key, value, immediately);
            } else {
                this.$message.error({
                    content: this.$t("message.noAuthorization"),
                    duration: 2,
                    key: "saved",
                });
            }
        },
        async setValue(key, value, immediately) {
            if (!this.settingInitialized || this.isBusy) {
                log.warn("setValue", "setting not initialized", { key, value });
                return;
            }
            const newSettings = await this.setSettingValue({
                setting: key,
                value,
            });
            if (immediately) {
                this.publishSettings(newSettings);
            } else {
                this.handleSave(newSettings);
            }
        },
    },
    mounted() {
        this.getSettings().then(() => {
            this.isBusy = false;
        });
        const settingsPubSub = systemPubSub.stream
            .pipe(filter((x) => x.type === "settings_page_changed"))
            .subscribe((x) => {
                this.tab = x.payload.name;
            });
        this.systemPubSubs.push(settingsPubSub);
        this.mountedTime = Date.now();
    },
    created() {
        log("Settings created");
    },
    updated() {
        // log("updated");
        if (this.$validator.locale !== this.$i18n.locale) {
            this.$validator.localize(this.$i18n.locale);
            this.$forceUpdate();
        }
    },
    beforeDestroy() {
        const supsis = window.supsis;
        if (!!supsis) supsis("destroy");
        this.systemPubSubs.map((sub) => sub.unsubscribe());
        this.isPreviewActive = false;
    },
    watch: {
        tab(value) {
            const selected = Object.values(SettingsMenuTypes).find((x) => x.id === value);
            if (selected) {
                this.$router.push(`/settings/${selected.path}`);
            }
        },
    },
};
</script>

<style scoped>
.dark-mode .settings-page-v2-wrapper {
    background-color: #343a40 !important;
}
.page-content{
    width: calc(100vw - 347px) !important;
}
</style>
